<template>
  <el-col
    v-if="filterConfig && showStatus"
    ref="entryEffectRef"
    :span="filterConfig.span || undefined"
    :style="colStyle"
    :class="{
      'col-wrap':true,
      'current':!disabled && currentElementIndexStr == itemIdxStr,
      'col-wrap-edit':!disabled,
      [filterConfig.classStyle||'']:true,
      'un-col-flex-wrap':!filterConfig.span && !filterConfig.flexOne
    }"
    :data-target-level-keys="disabled ? undefined : itemIdxStr"
    :data-can-move-up="(idx > 0) + ''"
    :data-can-move-down="(idx < listLength - 1) + ''"
    :data-component-name="'Col'"
    @mouseenter="hoverEffectEvents.mouseenter"
    @mouseleave="hoverEffectEvents.mouseleave"
    @click.capture="handleTrackingClick"
    @click="handleLowCodePopupClick"
  >
    <element-item
      v-if="filterConfig.items && filterConfig.items.length"
      v-for="(item, itemIdx) in filterConfig.items"
      :key="itemIdx"
      :disabled="!edit"
      :allArcListMap="getArcListMap(item)"
      :parentKeys="[...parentKeys, idx]"
      :item-data="item"
      :currentElementIndex="currentElementIndex"
      :idx="itemIdx"
      :list-length="filterConfig.items.length"
    ></element-item>
    <ClickConfigOverlay
      v-if="hasMeaningfulClickConfig(filterConfig.clickConfig)"
      :config="filterConfig.clickConfig"
    />
  </el-col>
</template>

<script setup>
import { computed, inject } from 'vue';
import { useElementIndex,toSetElementStyle } from './uses/elementIndexUse.js';
import { ElCol } from 'element-plus';
import 'element-plus/es/components/col/style/css';
import { useRenderConfig } from './uses/useRenderConfig.js';
import ClickConfigOverlay from './common/ClickConfigOverlay.vue';
import { useEntryEffect } from './uses/useEntryEffect.js';
import { useHoverEffect } from './uses/useHoverEffect.js';
import { buildCommonBoxStyle } from './uses/useResponsiveConfig.js';
import { isLowCodePopupPageAction } from '@/utils/lowCodePopupPage.js';
import { useLowCodeActionRuntime } from '@/composables/useLowCodeActionRuntime.js';
import { trackLowCodeEvent } from '@/utils/lowCodeTracking.js';

// 获取 props
const props = defineProps({
  allArcListMap: { type: Object, default: () => ({}) },
  parentKeys: {
    type: Array,
    default: () => [],
  },
  disabled: {
    type: Boolean,
    default: true,
  },
  config: {
    type: Object,
    default: () => ({}),
  },
  idx: {
    type: Number,
    default: -1,
  },
  listLength: {
    type: Number,
    default: 0,
  },
  currentElementIndex: {
    type: Array,
    default: () => [],
  },
});

// 使用 useElementIndex 来复用 index 逻辑
const {   currentElementIndexStr, itemIdxStr } = useElementIndex(props);
const { bindData, filterConfig, showStatus } = useRenderConfig(() => props.config);
const { dispatchLowCodeAction } = useLowCodeActionRuntime();
const getConfigJson = inject('getConfigJson', () => ({}));
const { targetRef: entryEffectRef, entryEffectStyle } = useEntryEffect(
  () => filterConfig.value || props.config || {},
);
const { hoverEffectEvents, wrapperHoverStyle, contentHoverStyle } = useHoverEffect(
  () => filterConfig.value || props.config || {},
);

// 编辑模式
const edit = computed(() => !props.disabled);

const handleTrackingClick = () => {
  if (!props.disabled) return;
  const element = filterConfig.value || props.config || {};
  const page = typeof getConfigJson === 'function' ? getConfigJson() : {};
  trackLowCodeEvent(element.trackingConfig, {
    trigger: 'click',
    element,
    elementType: 'col',
    elementIndex: itemIdxStr.value,
    page,
    binding: bindData.value,
  });
};

const handleLowCodePopupClick = (event) => {
  const action = filterConfig.value?.clickAction || props.config?.clickAction;
  if (!props.disabled || !isLowCodePopupPageAction(action)) return;
  event?.preventDefault?.();
  event?.stopPropagation?.();
  dispatchLowCodeAction(
    action,
    typeof getConfigJson === 'function' ? getConfigJson() : {},
  );
};

const hasMeaningfulClickConfig = (config) => {
  if (isLowCodePopupPageAction(config)) {
    return true;
  }
  if (!config || !config.responseType) {
    return false;
  }
  if (config.responseType === 'link' || config.responseType === 'jumpLink') {
    return !!config.link;
  }
  if (config.responseType === 'popup') {
    return !!config.article || !!config.popupTitle || !!config.popupClass;
  }
  if (config.responseType === 'popoverPage') {
    return (
      !!config.popoverPageId ||
      !!config.popoverPageTitle ||
      !!config.popoverPageData
    );
  }
  return true;
};

// 动态计算 Col 样式
const colStyle = computed(() => {
  const config = filterConfig.value || props.config || {};
  let setStyle = toSetElementStyle(config, bindData.value);
  const shouldUseCustomWidth =
    !config.span || config.widthMode === 'percent' || config.widthMode === 'fill';
  const flexOneStyle = config.flexOne
    ? {
        flex: '1 1 0%',
      }
    : {};
  return {
    ...buildCommonBoxStyle(
      {
        ...config,
        width: shouldUseCustomWidth ? config.width : undefined,
        widthMode: shouldUseCustomWidth ? config.widthMode : undefined,
      },
      {},
      { includePosition: true },
    ),
    flexDirection: config.isColumn ? 'column' : undefined,
    display:
      config.display ||
      (config.isFlex || config.isColumn ? 'flex' : undefined),
    justifyContent:
      config.isFlex || config.isColumn ? config.justifyContent : undefined,
    alignItems:
      config.isFlex || config.isColumn ? config.alignItems : undefined,
    flexWrap: config.flexWrap || undefined,
    gap:
      typeof config.gap === 'number' ? `${config.gap}px` : undefined,
    order:
      typeof config.order === 'number' ? String(config.order) : undefined,
    ...flexOneStyle,
    ...entryEffectStyle.value,
    ...wrapperHoverStyle.value,
    ...contentHoverStyle.value,
    ...setStyle,
  };
});

 

// 获取文章列表数据
const getArcListMap = (item) => {
  return item.type === 'articleListItem' || item.type === 'row' || item.type === 'col' || item.type === 'tabsItem'
    ? props.allArcListMap
    : undefined;
};
</script>

<style scoped>
.col-wrap {
  position: relative;
  background-size: 100% auto;
  background-position: center top;
}
 
.col-wrap-edit:hover::before {
  display: block;
}

.col-wrap-edit::before {
  content: " ";
  display: none;
  position: absolute;
  pointer-events: none;
  right: 0;
  left:0;
  top: 0;
  bottom: 0;
  z-index: 1112;
  border: 1px dashed #999;
}

.col-wrap-edit.current::before {
  display: block;
  border: 1px solid #276af4 !important;
 
}
 
  
.un-col-flex-wrap{
  flex: none;
}
</style>
